import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, Download, FilterList, GridOn, PictureAsPdf, Search } from '@mui/icons-material';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { reportsApi } from '../api/reportsApi';
import { getConfiguredApiUrl } from '../config/runtimeConfig';
import { masterApi } from '../api/masterApi';
import { employeeApi } from '../api/employeeApi';
import { DropdownItem, Employee } from '../types';
import { ReportFormState, ReportsTab, validateReportFilters } from '../modules/reports/utils/reportValidation';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

const attendanceStatusOptions = ['Present', 'Absent', 'HalfDay', 'Leave', 'CompOff'];
type SortDirection = 'asc' | 'desc';
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const dateRegex = /^\d{4}-\d{2}-\d{2}T00:00:00$/;
const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

const columnLabels: Record<string, string> = {
  firstPunch: 'Check In',
  lastPunch: 'Check Out',
  holidayDays: 'Holiday',
  weekOffDays: 'WeekOff',
  presentDays: 'Total Present Day',
  fullDayDays: 'Full Day',
  halfDayDays: 'Half Day',
  lateDays: 'Late Days',
  earlyCheckoutDays: 'Early Checkout Days',
  workedOnDayOffDays: 'Worked on WeekOff/Holiday',
};

const getColumnLabel = (col: string): string => {
  if (columnLabels[col]) return columnLabels[col];

  return col
    .replace(/([A-Z])/g, ' $1')   // split camelCase
    .replace(/_/g, ' ')          // snake_case support
    .replace(/\s+/g, ' ')        // clean spaces
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toMonthValue = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const getMonthRange = (monthValue: string): { fromDate: string; toDate: string } | null => {
  const match = monthValue.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }

  return {
    fromDate: formatDateInput(new Date(year, monthIndex, 1)),
    toDate: formatDateInput(new Date(year, monthIndex + 1, 0)),
  };
};

const getEmployeeLabel = (employee: Employee): string => {
  const name = employee.fullName || [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employee.employeeCode || 'Employee';
  return employee.employeeCode ? `${name} (${employee.employeeCode})` : name;
};

const formatPunchTime = (value: any): string => {
  if (value == null) return '-';
  const dt = new Date(String(value));
  if (!isNaN(dt.getTime())) {
    // const d = String(dt.getDate()).padStart(2, '0');
    // const mo = String(dt.getMonth() + 1).padStart(2, '0');
    // const y = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    const ss = String(dt.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
  return String(value);
};

const formatCellValue = (value: any): string => {
  if (value == null) return '-';
  const str = String(value);
  if (dateOnlyRegex.test(str)) {
    const [y, m, d] = str.split('-');
    return `${d}-${m}-${y}`;
  }
  if (dateRegex.test(str)) {
    const [y, m, d] = str.substring(0, 10).split('-');
    return `${d}-${m}-${y}`;
  }
  if (dateTimeRegex.test(str)) {
    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      const d = String(dt.getDate()).padStart(2, '0');
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const y = dt.getFullYear();
      const hh = String(dt.getHours()).padStart(2, '0');
      const mm = String(dt.getMinutes()).padStart(2, '0');
      return `${d}-${m}-${y}`;
    }
  }
  return str;
};

const getComparableValue = (value: any): string | number => {
  const formatted = formatCellValue(value);
  const trimmed = formatted.trim();
  const hourMatch = trimmed.match(/^(\d+)h\s+(\d+)m$/i);
  if (hourMatch) return Number(hourMatch[1]) * 60 + Number(hourMatch[2]);
  const numeric = Number(trimmed.replace(/,/g, ''));
  if (trimmed !== '' && !Number.isNaN(numeric)) return numeric;
  const dateMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dateMatch) return new Date(Number(dateMatch[3]), Number(dateMatch[2]) - 1, Number(dateMatch[1])).getTime();
  return trimmed.toLowerCase();
};

const ReportsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const permissions = user?.permissions ?? [];
  const canViewAllReports = permissions.includes('ViewReports');
  const [tab, setTab] = useState<ReportsTab>('attendance');
  //const [form, setForm] = useState<ReportFormState>({ fromDate: '', toDate: '', searchTerm: '' });
  const [department, setDepartment] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [status, setStatus] = useState('');
  const [deviceBased, setDeviceBased] = useState('');
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Record<string, any>>({});
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [errors, setErrors] = useState({
  fromDate: false,
  toDate: false,
});
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
  const [gridSearch, setGridSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: SortDirection } | null>(null);
  const getToday = () => new Date().toISOString().split('T')[0];
  const [form, setForm] = useState<ReportFormState>({
  fromDate: getToday(),   // ✅ default today
  toDate: getToday(),     // ✅ default today
  searchTerm: '',
});

  const monthOptions = useMemo(() => {
    const current = new Date();
    current.setDate(1);

    return Array.from({ length: 24 }, (_, index) => {
      const date = new Date(current.getFullYear(), current.getMonth() - index, 1);
      return {
        value: toMonthValue(date),
        label: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
      };
    });
  }, []);

 useEffect(() => {
  const style = document.createElement('style');

  style.innerHTML = `
    @media print {

      body {
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      /* Hide everything */
      body * {
        visibility: hidden;
      }

      /* Show only report section */
      .report-print-area,
      .report-print-area * {
        visibility: visible;
      }

      .report-print-area {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: white;
        padding: 10px;
      }

      /* Hide buttons, tabs, pagination */
      button,
      .MuiTabs-root,
      .MuiTablePagination-root,
      .MuiDialog-root {
        display: none !important;
      }

      /* Remove table scroll */
      .MuiTableContainer-root {
        overflow: visible !important;
        max-height: none !important;
      }

      table {
        width: 100% !important;
        border-collapse: collapse !important;
      }

      th,
      td {
        border: 1px solid #ccc !important;
        padding: 6px !important;
        font-size: 12px !important;
        white-space: nowrap !important;
      }

      thead {
        display: table-header-group;
      }

      tr {
        page-break-inside: avoid;
      }

      .MuiPaper-root,
      .MuiCard-root {
        box-shadow: none !important;
      }
    }
  `;

  document.head.appendChild(style);

  return () => {
    document.head.removeChild(style);
  };
}, []);

  // const columns = useMemo(() => {
  //   if (!rows.length) return [] as string[];
  //   const keys = Object.keys(rows[0])
  //     .filter((col) => col.toLowerCase() !== 'employeeid' && col.toLowerCase() !== 'leavedays' && col.toLowerCase() !== 'leaveusage');
  //   if (tab === 'overtime') return keys.filter((col) => col.toLowerCase() !== 'department');
  //   return keys;
  // }, [rows, tab]);

  const columns = useMemo(() => {
  if (!rows.length) return [] as string[];

  const keys = Object.keys(rows[0]).filter(
    (col) =>
      col.toLowerCase() !== 'employeeid' &&
      col.toLowerCase() !== 'leavedays' &&
      col.toLowerCase() !== 'leaveusage'
  );

  // ✅ REMOVE punchTime ONLY for Device-wise Report
  if (tab === 'mobileCheckin') {
    return keys.filter(
      (col) =>
        col.toLowerCase() !== 'punchtime' &&
        col !== 'firstPunch' &&
        col !== 'lastPunch' 
    );
  }

  if (tab === 'overtime') {
    return keys.filter((col) => col.toLowerCase() !== 'department');
  }

  return keys;
}, [rows, tab]);

  useEffect(() => {
    masterApi.getDepartments().then(setDepartments).catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    if (!canViewAllReports) {
      setEmployees([]);
      return;
    }

    let isCancelled = false;
    setEmployeesLoading(true);

    employeeApi.getAll(1, 10000)
      .then((result) => {
        if (!isCancelled) setEmployees(result);
      })
      .catch(() => {
        if (!isCancelled) setEmployees([]);
      })
      .finally(() => {
        if (!isCancelled) setEmployeesLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [canViewAllReports]);

  const employeeOptions = useMemo(() => {
    const scopedEmployees = department
      ? employees.filter((employee) => String(employee.departmentId || '') === department)
      : employees;

    return [...scopedEmployees].sort((a, b) => getEmployeeLabel(a).localeCompare(getEmployeeLabel(b)));
  }, [department, employees]);

  const selectedEmployee = useMemo(
    () => employeeOptions.find((employee) => employee.id === employeeId) || employees.find((employee) => employee.id === employeeId) || null,
    [employeeId, employeeOptions, employees]
  );

  useEffect(() => {
    if (!department || !employeeId) return;

    const employee = employees.find((item) => item.id === employeeId);
    if (employee?.departmentId && String(employee.departmentId) !== department) {
      setEmployeeId('');
    }
  }, [department, employeeId, employees]);

  useEffect(() => { setPage(0); }, [rows]);
  useEffect(() => { setPage(0); }, [columnFilters, gridSearch, sortConfig, tab]);

  useEffect(() => {
    if (columns.length > 0) {
      setVisibleColumns(columns);
      setColumnFilters((prev) =>
        columns.reduce<Record<string, string>>((next, col) => {
          if (prev[col]) next[col] = prev[col];
          return next;
        }, {})
      );
    } else {
      setVisibleColumns([]);
      setColumnFilters({});
    }
  }, [columns]);

  const filteredRows = useMemo(() => {
    const searchableColumns = tab === 'consolidated' ? visibleColumns : columns;
    const searchValue = gridSearch.trim().toLowerCase();
    const searchedRows = searchValue
      ? rows.filter((row) =>
          searchableColumns.some((column) =>
            (column === 'firstPunch' || column === 'lastPunch'
              ? formatPunchTime(row[column])
              : formatCellValue(row[column])
            ).toLowerCase().includes(searchValue)
          )
        )
      : rows;

    if (tab !== 'consolidated') return searchedRows;

    const activeFilters = Object.entries(columnFilters)
      .filter(([column]) => visibleColumns.includes(column))
      .map(([column, value]) => [column, value.trim().toLowerCase()] as const)
      .filter(([, value]) => value.length > 0);

    if (activeFilters.length === 0) return searchedRows;

    return searchedRows.filter((row) =>
      activeFilters.every(([column, filterValue]) =>
        (column === 'firstPunch' || column === 'lastPunch'
          ? formatPunchTime(row[column])
          : formatCellValue(row[column])
        ).toLowerCase().includes(filterValue)
      )
    );
  }, [rows, columnFilters, gridSearch, tab, visibleColumns, columns]);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aValue = getComparableValue(sortConfig.column === 'firstPunch' || sortConfig.column === 'lastPunch'
        ? formatPunchTime(a[sortConfig.column])
        : a[sortConfig.column]);
      const bValue = getComparableValue(sortConfig.column === 'firstPunch' || sortConfig.column === 'lastPunch'
        ? formatPunchTime(b[sortConfig.column])
        : b[sortConfig.column]);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const compare = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
      return sortConfig.direction === 'asc' ? compare : -compare;
    });
  }, [filteredRows, sortConfig]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const visibleFrom = filteredRows.length === 0 ? 0 : page * rowsPerPage + 1;
  const visibleTo = Math.min((page + 1) * rowsPerPage, filteredRows.length);

  const toImageUrl = (path: any) => {
    if (!path) return null;
    const str = String(path);
    if (/^https?:\/\//i.test(str)) return str;
    const apiBase = getConfiguredApiUrl().replace(/\/?api\/?$/i, '');
    return `${apiBase}/${str.replace(/^\/+/, '')}`;
  };

  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);

    const range = getMonthRange(value);
    if (!range) return;

    setForm((prev) => ({
      ...prev,
      fromDate: range.fromDate,
      toDate: range.toDate,
    }));
    setErrors({ fromDate: false, toDate: false });
  };

  const handleGenerate = async () => {

     let newErrors = {
    fromDate: !form.fromDate,
    toDate: !form.toDate,
  };

  // Optional: check if From Date > To Date
  if (form.fromDate && form.toDate && form.fromDate > form.toDate) {
    newErrors.toDate = true;
    alert("To Date should be greater than or equal to From Date");
  }

  setErrors(newErrors);
  if (newErrors.fromDate || newErrors.toDate) return;
  console.log("Form submitted", form);

    const validationMessage = validateReportFilters(tab, form);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (tab === 'attendance') {
        const result = await reportsApi.getAttendance({
          fromDate: form.fromDate || undefined,
          toDate: form.toDate || undefined,
          departmentId: department || undefined,
          employeeId: employeeId || undefined,
          status: status || undefined,
        });

        setSummary(result?.summary ?? {});
        setRows(result?.data ?? []);
      }

      if (tab === 'leaveUsage') {
        const result = await reportsApi.getLeaveUsage({
          departmentId: department || undefined,
          employeeId: employeeId || undefined,
          searchTerm: form.searchTerm || undefined,
        });

        setSummary({ totalRecords: result?.totalRecords ?? 0 });
        setRows(result?.data ?? []);
      }

      if (tab === 'mobileCheckin') {
        const result = await reportsApi.getMobileCheckin({
          employeeId: employeeId || undefined,
          fromDate: form.fromDate || undefined,
          toDate: form.toDate || undefined,
          departmentId: department || undefined,
          deviceBased: deviceBased || undefined,
        });

        setSummary({ totalRecords: result?.totalRecords ?? 0, fromDate: form.fromDate, toDate: form.toDate });
        setRows(result?.data ?? []);
      }

      if (tab === 'punch') {
        const result = await reportsApi.getPunchReport({
          employeeId: employeeId || undefined,
          fromDate: form.fromDate || undefined,
          toDate: form.toDate || undefined,
          departmentId: department || undefined,
        });

        setSummary({ totalRecords: result?.totalRecords ?? 0 });
        setRows(result?.data ?? []);
      }

      if (tab === 'overtime') {
        const result = await reportsApi.getOvertimeReport({
          employeeId: employeeId || undefined,
          fromDate: form.fromDate || undefined,
          toDate: form.toDate || undefined,
          departmentId: department || undefined,
        });

        setSummary(result?.summary ?? {});
        setRows(result?.data ?? []);
      }

      if (tab === 'consolidated') {
        const result = await reportsApi.getConsolidatedReport({
          employeeId: employeeId || undefined,
          fromDate: form.fromDate || undefined,
          toDate: form.toDate || undefined,
          departmentId: department || undefined,
        });

        setSummary(result?.summary ?? {});
        setRows(result?.data ?? []);
      }

      if (tab === 'alerts') {
        const result = await reportsApi.getAlertsReport({
          employeeId: employeeId || undefined,
          fromDate: form.fromDate || undefined,
          toDate: form.toDate || undefined,
          departmentId: department || undefined,
        });

        setSummary(result?.summary ?? {});
        setRows(result?.data ?? []);
      }
    } catch (err: any) {
      setRows([]);
      setSummary({});
      setError(err?.response?.data?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  };
  

  const getExportFileName = (extension: 'csv' | 'xlsx' | 'pdf') =>
    `${tab}_report_${new Date().toISOString().slice(0, 10)}.${extension}`;

  const getFormattedExportValue = (row: any, col: string) =>
    col === 'firstPunch' || col === 'lastPunch'
      ? formatPunchTime(row[col])
      : formatCellValue(row[col]);

const getExportRows = () => {
    const cols = tab === 'consolidated' ? visibleColumns : columns;
    return sortedRows.map((row, index) => {
      const exportRow: Record<string, string | number> = { '#': index + 1 };
      cols.forEach((col) => {
        exportRow[getColumnLabel(col)] = getFormattedExportValue(row, col);
      });
      return exportRow;
    });
  };

  const handleExportCsv = () => {
    const cols = tab === 'consolidated' ? visibleColumns : columns;
    const header = ['#', ...cols.map(getColumnLabel)].join(',');
    const csvRows = sortedRows.map((row, index) =>
      [index + 1, ...cols.map((col) => getFormattedExportValue(row, col))]
        .map((val) => `"${String(val).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getExportFileName('csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    const exportRows = getExportRows();
    const exportColumns = Object.keys(exportRows[0] || {});
    const ws = XLSX.utils.json_to_sheet(exportRows, { header: exportColumns });
    ws['!cols'] = exportColumns.map((key) => ({
      wch: Math.min(Math.max(key.length, ...exportRows.map((row) => String(row[key] ?? '').length)) + 3, 40),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), getExportFileName('xlsx'));
  };

const handleExportPdf = () => {
    const exportRows = getExportRows();
    const exportColumns = Object.keys(exportRows[0] || {});
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(getColumnLabel(`${tab} report`), 14, 14);
    doc.setFontSize(9);
    doc.text(`Period: ${form.fromDate || '-'} to ${form.toDate || '-'} | Total Records: ${filteredRows.length}`, 14, 21);
    autoTable(doc, {
      startY: 27,
      head: [exportColumns],
      body: exportRows.map((row) => exportColumns.map((column) => String(row[column] ?? ''))),
      styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    doc.save(getExportFileName('pdf'));
  };

  const displayColumns = useMemo(() => {
    return tab === 'consolidated' ? visibleColumns : columns;
  }, [tab, visibleColumns, columns]);

  const activeColumnFiltersCount = useMemo(
    () => Object.values(columnFilters).filter((value) => value.trim().length > 0).length,
    [columnFilters]
  );

  const handleOpenColumnFilter = (event: React.MouseEvent<HTMLElement>, column: string) => {
    if (tab !== 'consolidated') return;
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleCloseColumnFilter = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleClearColumnFilter = (column: string) => {
    setColumnFilters((prev) => ({ ...prev, [column]: '' }));
  };

  const handleSort = (column: string) => {
    setSortConfig((current) => ({
      column,
      direction: current?.column === column && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    //<Box>
      <Box className="report-print-area">
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Reports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reports for attendance, leave usage, mobile check-ins, punches, overtime, consolidated summaries, and alerts.
      </Typography>

      <Paper sx={{ mb: 2 }}>
       <Tabs
          value={tab}
          onChange={(_, value) => {
            setTab(value);
            setRows([]);
            setSummary({});
            setError(null);
            setGridSearch('');
            setSortConfig(null);
            setColumnFilters({});
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
          }}
        >  
        <Tab label="Attendance" value="attendance" />
          <Tab label="Leave Usage" value="leaveUsage" />
          <Tab label="Device-wise Report" value="mobileCheckin" />
          <Tab label="Punch Report" value="punch" />
          <Tab label="Overtime" value="overtime" />
          <Tab label="Consolidated Report" value="consolidated" />
          <Tab label="Late Marks" value="alerts" />
        </Tabs>
      </Paper>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => handleMonthChange(String(e.target.value))}
                >
                  <MenuItem value="">Custom Date Range</MenuItem>
                  {monthOptions.map((month) => (
                    <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
               <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.fromDate}
                  error={errors.fromDate}
                  helperText={errors.fromDate ? "From Date is required" : ""}
                  onChange={(e) => {
                    setSelectedMonth('');
                    setForm((prev) => ({ ...prev, fromDate: e.target.value }));
                  }}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
               <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.toDate}
                  error={errors.toDate}
                  helperText={errors.toDate ? "To Date is required" : ""}
                  onChange={(e) => {
                    setSelectedMonth('');
                    setForm((prev) => ({ ...prev, toDate: e.target.value }));
                  }}
                />
            </Grid>
            {tab !== 'overtime' && canViewAllReports && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={department}
                    label="Department"
                    onChange={(e) => setDepartment(String(e.target.value))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {canViewAllReports && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  options={employeeOptions}
                  loading={employeesLoading}
                  value={selectedEmployee}
                  onChange={(_, value) => setEmployeeId(value?.id || '')}
                  getOptionLabel={getEmployeeLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No employees found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee"
                      placeholder="All Employees"
                    />
                  )}
                />
              </Grid>
            )}

            {tab === 'attendance' && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} label="Status" onChange={(e) => setStatus(String(e.target.value))}>
                    <MenuItem value="">All</MenuItem>
                    {attendanceStatusOptions.map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {tab === 'leaveUsage' && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Search (Employee Name / Code)"
                  value={form.searchTerm}
                  onChange={(e) => setForm((prev) => ({ ...prev, searchTerm: e.target.value }))}
                />
              </Grid>
            )}

{tab === 'mobileCheckin' && (
               <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                 <FormControl fullWidth>
                   <InputLabel>Device Based</InputLabel>
                   <Select value={deviceBased} label="Device Based" onChange={(e) => setDeviceBased(String(e.target.value))}>
                     <MenuItem value="">All</MenuItem>
                     <MenuItem value="Web Based Entry">Web Based Entry</MenuItem>
                     <MenuItem value="Mobile App">Mobile App</MenuItem>
                     {/* {attendanceStatusOptions.map((item) => (
                       <MenuItem key={item} value={item}>{item}</MenuItem>
                     ))} */}
                   </Select>
                 </FormControl>
               </Grid>
             )}

             {tab === 'consolidated' && columns.length > 0 && (
               <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                 <FormControl fullWidth>
                   <InputLabel>Columns</InputLabel>
                   <Select
                     multiple
                     value={visibleColumns}
                     onChange={(e) => setVisibleColumns(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                     label="Columns"
                     renderValue={() => `${visibleColumns.length} selected`}
                   >
                     {columns.map((col) => (
                       <MenuItem key={col} value={col}>
                         <Checkbox checked={visibleColumns.indexOf(col) > -1} />
                         <ListItemText primary={getColumnLabel(col)} />
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
               </Grid>
             )}

             <Grid size={{ xs: 12 }}>
              <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={handleGenerate}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
          </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* {Object.keys(summary).length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Summary</Typography>
            <Grid container spacing={1}>
              {Object.entries(summary)
                .filter(([key]) => key !== 'totalRecords' && key !== 'leaveCount')
                .map(([key, value]) => (
                  <Grid key={key} size={{ xs: 12, sm: 6, md: 3 }}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">{key}</Typography>
                      <Typography variant="body1" fontWeight={600}>{formatCellValue(value)}</Typography>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </CardContent>
        </Card>
      )} */}

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 1.5,
          p: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(56, 142, 60, 0.06))',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Total Records: {filteredRows.length}
            {filteredRows.length !== rows.length && ` of ${rows.length}`}
            {filteredRows.length > 0 && ` | Showing ${visibleFrom}-${visibleTo}`}
            {tab === 'consolidated' && activeColumnFiltersCount > 0 && ` | Filters: ${activeColumnFiltersCount}`}
          </Typography>
          {rows.length > 0 && (
            <TextField
              size="small"
              value={gridSearch}
              onChange={(e) => setGridSearch(e.target.value)}
              placeholder="Search grid..."
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
        {filteredRows.length > 0 && displayColumns.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tab === 'consolidated' && activeColumnFiltersCount > 0 && (
              <Button
                variant="text"
                onClick={() => setColumnFilters({})}
                size="small"
                sx={{ borderRadius: 999, px: 2 }}
              >
                Clear filters
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportCsv}
              size="small"
              sx={{ borderRadius: 999, px: 2 }}
            >
              CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<GridOn />}
              onClick={handleExportExcel}
              size="small"
              sx={{ borderRadius: 999, px: 2 }}
            >
              Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPdf}
              size="small"
              sx={{ borderRadius: 999, px: 2 }}
            >
              PDF
            </Button>
          </Box>
        )}
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <Table size="small" stickyHeader>
          {/* <TableHead>
            <TableRow>
              {columns.length === 0 ? (
                <TableCell
                  component="th"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'grey.100',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  No data columns
                </TableCell>
              ) : (
                columns.map((col) => (
                  <TableCell
                    key={col}
                    component="th"
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'grey.100',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getColumnLabel(col)}
                  </TableCell>
                ))
              )}
            </TableRow>
</TableHead> */}

          <TableHead>
            <TableRow>
              {displayColumns.length === 0 ? (
                <TableCell
                  component="th"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'grey.100',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  No data columns
                </TableCell>
              ) : displayColumns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.8rem',
                    bgcolor: 'grey.100',
                    textTransform: 'capitalize',
                    letterSpacing: 0.6,
                    color: 'text.secondary',
                    py: 1.2,
                    minWidth: tab === 'consolidated' ? 150 : undefined,
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      width: '100%',
                      color: columnFilters[col]?.trim() ? 'primary.main' : 'inherit',
                    }}
                  >
                    <TableSortLabel
                      active={sortConfig?.column === col}
                      direction={sortConfig?.column === col ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort(col)}
                      sx={{
                        flex: 1,
                        '& .MuiTableSortLabel-icon': { fontSize: '1rem' },
                      }}
                    >
                      {getColumnLabel(col)}
                    </TableSortLabel>
                    {tab === 'consolidated' && (
                      <Tooltip title="Filter this column">
                        <IconButton
                          size="small"
                          onClick={(event) => handleOpenColumnFilter(event, col)}
                          sx={{ p: 0.25 }}
                        >
                          <FilterList
                            fontSize="small"
                            color={columnFilters[col]?.trim() ? 'primary' : 'disabled'}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayColumns.length === 0 ? (
              <TableRow>
                <TableCell align="center">
                  Select at least one column to view the report.
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow
                hover
                sx={{
                  '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                  '& td': {
                    fontSize: '0.85rem',
                    py: 1,
                  },
                }}
              >
                <TableCell colSpan={Math.max(1, displayColumns.length)} align="center">
                  {rows.length > 0 ? 'No records match the current search or filters.' : 'No report data found.'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, index) => (
                <TableRow
                  key={page * rowsPerPage + index}
                  hover
                  sx={{
                    '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                    '& td': { borderColor: 'divider' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {displayColumns.map((col) => (
                    <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const key = String(col || '').toLowerCase();
                        if (key.includes('selfie')) {
                          const url = toImageUrl(row[col]);
                          if (url) {
                            return (
                              <img
                                src={url}
                                alt="Selfie"
                                loading="lazy"
                                onClick={() => setPreviewUrl(url)}
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                              />
                            );
                          }
                          return <span style={{ color: 'text.secondary' }}>No Image</span>;
                        }

                        if (col === 'firstPunch' || col === 'lastPunch') return formatPunchTime(row[col]);
                        return formatCellValue(row[col]);
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Popover
        open={Boolean(filterAnchorEl && activeFilterColumn)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseColumnFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {activeFilterColumn && (
          <Box sx={{ p: 2, width: 300 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Filter {getColumnLabel(activeFilterColumn)}
              </Typography>
              <IconButton size="small" onClick={handleCloseColumnFilter} aria-label="Close column filter">
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              autoFocus
              fullWidth
              value={columnFilters[activeFilterColumn] ?? ''}
              onChange={(e) => setColumnFilters((prev) => ({ ...prev, [activeFilterColumn]: e.target.value }))}
              placeholder={`Search ${getColumnLabel(activeFilterColumn)}`}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Matching rows update as you type."
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button size="small" onClick={() => handleClearColumnFilter(activeFilterColumn)}>
                Clear
              </Button>
              <Button size="small" variant="contained" onClick={handleCloseColumnFilter}>
                Done
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
      {filteredRows.length > 0 && (
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            '& .MuiTablePagination-toolbar': { minHeight: 56 },
          }}
        />
      )}
      {/* Image preview modal */}
      <Dialog open={Boolean(previewUrl)} onClose={() => setPreviewUrl(null)} maxWidth="md" fullWidth>
        <DialogContent>
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" style={{ width: '100%', height: 'auto', borderRadius: 6 }} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsPage;
